import React from 'react';

interface ProcessingConfig {
  num_colors: number;
  target_width: number;
  yarn_weight: number;
  padding_type: 'none' | 'stitches' | 'inches' | 'auto';
  padding_horizontal: number;
  padding_vertical: number;
  blanket_size: string;
  custom_width: number;
  custom_height: number;
}

interface ConfigurationPanelProps {
  config: ProcessingConfig;
  onChange: (newConfig: Partial<ProcessingConfig>) => void;
  disabled: boolean;
  hasConfigChanged?: boolean;
  onRecalculate?: () => void;
}

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  config,
  onChange,
  disabled,
  hasConfigChanged,
  onRecalculate
}) => {
  const yarnWeights = [
    { value: 1, label: '1 - Lace' },
    { value: 2, label: '2 - Sport' },
    { value: 3, label: '3 - DK' },
    { value: 4, label: '4 - Worsted' },
    { value: 5, label: '5 - Chunky' },
    { value: 6, label: '6 - Super Bulky' },
    { value: 7, label: '7 - Jumbo' }
  ];

  const blanketSizes = [
    { value: 'baby', label: 'Baby Blanket', width: 30, height: 40 },
    { value: 'lap', label: 'Lap Blanket', width: 36, height: 48 },
    { value: 'throw', label: 'Throw Blanket', width: 50, height: 60 },
    { value: 'twin', label: 'Twin Bed', width: 68, height: 86 },
    { value: 'full', label: 'Full/Queen Bed', width: 90, height: 90 },
    { value: 'king', label: 'King Bed', width: 108, height: 90 },
    { value: 'custom', label: 'Custom Size', width: 0, height: 0 }
  ];

  const getStitchesFromBlanketSize = (sizeKey: string) => {
    const size = blanketSizes.find(s => s.value === sizeKey);
    if (!size || size.value === 'custom') return config.target_width;
    
    // Calculate stitches based on yarn weight gauge
    const gaugeMap: { [key: number]: number } = {
      1: 8,   // Lace - 8 stitches per inch
      2: 6,   // Sport - 6 stitches per inch
      3: 5.5, // DK - 5.5 stitches per inch
      4: 4,   // Worsted - 4 stitches per inch
      5: 3,   // Chunky - 3 stitches per inch
      6: 2,   // Super Bulky - 2 stitches per inch
      7: 1.5  // Jumbo - 1.5 stitches per inch
    };
    
    const stitchesPerInch = gaugeMap[config.yarn_weight] || 4;
    return Math.round(size.width * stitchesPerInch);
  };

  const handleBlanketSizeChange = (newSize: string) => {
    const selectedSize = blanketSizes.find(s => s.value === newSize);
    if (selectedSize && selectedSize.value !== 'custom') {
      const targetWidth = getStitchesFromBlanketSize(newSize);
      onChange({ 
        blanket_size: newSize,
        target_width: targetWidth,
        custom_width: selectedSize.width,
        custom_height: selectedSize.height
      });
    } else {
      onChange({ blanket_size: newSize });
    }
  };

  return (
    <div className="configuration-panel">
      <h2>Pattern Settings</h2>
      
      {hasConfigChanged && onRecalculate && (
        <div className="recalculate-prompt" style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #1976d2',
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '16px',
          color: '#1565c0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong>⚙️ Settings Changed:</strong> Your pattern settings have been modified.
              <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                Click "Recalculate Pattern" to apply the new settings to your uploaded image.
              </div>
            </div>
            <button
              onClick={onRecalculate}
              disabled={disabled}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                whiteSpace: 'nowrap',
                marginLeft: '12px'
              }}
            >
              {disabled ? 'Recalculating...' : 'Recalculate Pattern'}
            </button>
          </div>
        </div>
      )}
      
      <div className="config-group">
        <label htmlFor="blanket-size">Blanket Size:</label>
        <select
          id="blanket-size"
          value={config.blanket_size}
          onChange={(e) => handleBlanketSizeChange(e.target.value)}
          disabled={disabled}
        >
          {blanketSizes.map(size => (
            <option key={size.value} value={size.value}>
              {size.label} {size.value !== 'custom' ? `(${size.width}" × ${size.height}")` : ''}
            </option>
          ))}
        </select>
      </div>

      {config.blanket_size === 'custom' && (
        <>
          <div className="config-group">
            <label htmlFor="custom-width">Custom Width (inches):</label>
            <input
              id="custom-width"
              type="number"
              min="12"
              max="120"
              value={config.custom_width}
              onChange={(e) => onChange({ custom_width: parseInt(e.target.value) })}
              disabled={disabled}
            />
          </div>
          <div className="config-group">
            <label htmlFor="custom-height">Custom Height (inches):</label>
            <input
              id="custom-height"
              type="number"
              min="12"
              max="120"
              value={config.custom_height}
              onChange={(e) => onChange({ custom_height: parseInt(e.target.value) })}
              disabled={disabled}
            />
          </div>
        </>
      )}
      
      <div className="config-group">
        <label htmlFor="num-colors">Number of Colors:</label>
        <select
          id="num-colors"
          value={config.num_colors}
          onChange={(e) => onChange({ num_colors: parseInt(e.target.value) })}
          disabled={disabled}
        >
          <option value={2}>2 Colors</option>
          <option value={3}>3 Colors</option>
          <option value={4}>4 Colors</option>
          <option value={5}>5 Colors</option>
          <option value={6}>6 Colors</option>
        </select>
      </div>

      {config.blanket_size === 'custom' && (
        <div className="config-group">
          <label htmlFor="target-width">Pattern Width (stitches):</label>
          <input
            id="target-width"
            type="number"
            min="20"
            max="400"
            value={config.target_width}
            onChange={(e) => onChange({ target_width: parseInt(e.target.value) })}
            disabled={disabled}
          />
          <small style={{color: '#666', fontSize: '0.8rem'}}>
            Calculated automatically for standard sizes
          </small>
        </div>
      )}

      <div className="config-group">
        <label htmlFor="yarn-weight">Yarn Weight:</label>
        <select
          id="yarn-weight"
          value={config.yarn_weight}
          onChange={(e) => onChange({ yarn_weight: parseInt(e.target.value) })}
          disabled={disabled}
        >
          {yarnWeights.map(weight => (
            <option key={weight.value} value={weight.value}>
              {weight.label}
            </option>
          ))}
        </select>
      </div>

      <div className="config-group">
        <label htmlFor="padding-type">Border/Padding:</label>
        <select
          id="padding-type"
          value={config.padding_type}
          onChange={(e) => onChange({ padding_type: e.target.value as 'none' | 'stitches' | 'inches' | 'auto' })}
          disabled={disabled}
        >
          <option value="auto">Automatic padding (recommended)</option>
          <option value="none">No border</option>
          <option value="stitches">Manual border (stitches)</option>
          <option value="inches">Manual border (inches)</option>
        </select>
      </div>

      {config.padding_type === 'auto' && (
        <div className="config-group">
          <small style={{color: '#666', fontSize: '0.8rem'}}>
            Automatically adds appropriate padding to center the image within the selected blanket size while maintaining aspect ratio.
          </small>
        </div>
      )}

      {(config.padding_type === 'stitches' || config.padding_type === 'inches') && (
        <>
          <div className="config-group">
            <label htmlFor="padding-horizontal">
              Horizontal Border ({config.padding_type}):
            </label>
            <input
              id="padding-horizontal"
              type="number"
              min="0"
              max={config.padding_type === 'stitches' ? '50' : '12'}
              value={config.padding_horizontal}
              onChange={(e) => onChange({ padding_horizontal: parseInt(e.target.value) })}
              disabled={disabled}
            />
          </div>
          <div className="config-group">
            <label htmlFor="padding-vertical">
              Vertical Border ({config.padding_type}):
            </label>
            <input
              id="padding-vertical"
              type="number"
              min="0"
              max={config.padding_type === 'stitches' ? '50' : '12'}
              value={config.padding_vertical}
              onChange={(e) => onChange({ padding_vertical: parseInt(e.target.value) })}
              disabled={disabled}
            />
          </div>
          <small style={{color: '#666', fontSize: '0.8rem'}}>
            {config.padding_type === 'stitches' 
              ? 'Adds solid color border around the pattern' 
              : 'Converts inches to stitches based on yarn weight'}
          </small>
        </>
      )}

      <div className="config-info">
        <h3>Current Settings:</h3>
        <ul>
          <li>Blanket: {blanketSizes.find(s => s.value === config.blanket_size)?.label}</li>
          {config.blanket_size === 'custom' ? (
            <li>Size: {config.custom_width}" × {config.custom_height}"</li>
          ) : (
            <li>Size: {blanketSizes.find(s => s.value === config.blanket_size)?.width}" × {blanketSizes.find(s => s.value === config.blanket_size)?.height}"</li>
          )}
          <li>Colors: {config.num_colors}</li>
          <li>Width: {config.target_width} stitches</li>
          <li>Yarn: {yarnWeights.find(w => w.value === config.yarn_weight)?.label}</li>
          <li>Border: {
            config.padding_type === 'none' ? 'None' :
            config.padding_type === 'auto' ? 'Automatic' :
            `H:${config.padding_horizontal}, V:${config.padding_vertical} ${config.padding_type}`
          }</li>
        </ul>
      </div>
    </div>
  );
};

export default ConfigurationPanel;